import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useMeMantras } from "@/hooks/use-me-queries";
import { getMeSession } from "@/lib/auth/storage";
import { meApi } from "@/lib/api/me";
import { formatDateDisplay } from "@/lib/timezone";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import type { MantraDay } from "@/lib/types";

function dayLabel(day: MantraDay): string {
  const kind = day.trigger_status === "L" ? "Late" : "Absent";
  return `${formatDateDisplay(day.date)} · ${kind}`;
}

export default function MeMantrasPage() {
  const session = getMeSession();
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useMeMantras();
  const [selected, setSelected] = useState<string[]>([]);
  const [notes, setNotes] = useState("");

  const submit = useMutation({
    mutationFn: () => meApi.submitMantras(session!, selected, notes.trim() || undefined),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["me", "mantras"] });
      setSelected([]);
      setNotes("");
    },
  });

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  }
  if (error || !data) {
    return <p className="text-sm text-red-600">Could not load mahamantra status.</p>;
  }
  if (!data.applies) {
    return (
      <div className="space-y-3">
        <h2 className="text-xl font-semibold">Mahamantras</h2>
        <p className="text-sm text-muted-foreground">
          This is only for Morning Yoga. Your other groups do not have this
          write-up or fine.
        </p>
      </div>
    );
  }

  const selectable = [...data.due, ...data.fined];
  const allClear =
    data.due.length === 0 &&
    data.submitted.length === 0 &&
    data.fined.length === 0;

  function toggle(dateStr: string) {
    setSelected((prev) =>
      prev.includes(dateStr)
        ? prev.filter((d) => d !== dateStr)
        : [...prev, dateStr]
    );
  }

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-semibold">Mahamantras</h2>
        <p className="text-sm text-muted-foreground">
          For Yoga morning, Late or Absent means write mahamantras that same
          day. From the next day it is a ₹{data.fine_rupees} fine.
        </p>
      </div>

      <Card className={data.fine_total_rupees > 0 ? "border-red-200" : "border-saffron-100"}>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Fine so far</CardTitle>
          <CardDescription>
            ₹{data.fine_rupees} × each L/A day not submitted on that day
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p
            className={`text-3xl font-bold tabular-nums ${
              data.fine_total_rupees > 0 ? "text-red-700" : "text-emerald-700"
            }`}
          >
            ₹{data.fine_total_rupees}
          </p>
          {data.fined.length > 0 && (
            <p className="mt-1 text-sm text-muted-foreground">
              {data.fined.length} day{data.fined.length === 1 ? "" : "s"} overdue
            </p>
          )}
        </CardContent>
      </Card>

      {allClear && (
        <p className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-3 text-sm text-emerald-800">
          Nothing outstanding — no mantras due and no fine.
        </p>
      )}

      {data.due.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Write mahamantras</CardTitle>
            <CardDescription>
              Submit after you have written them. Coordinator will confirm and
              waive the fine.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {data.due.map((day) => (
              <label
                key={day.date}
                className="flex items-start gap-2 rounded-md border p-2 text-sm"
              >
                <Checkbox
                  checked={selected.includes(day.date)}
                  onCheckedChange={() => toggle(day.date)}
                />
                <span>
                  <span className="font-medium">{dayLabel(day)}</span>
                  <span className="mt-0.5 block text-xs text-muted-foreground">
                    Write today · ₹{day.fine_rupees} from tomorrow if missed
                  </span>
                </span>
              </label>
            ))}
          </CardContent>
        </Card>
      )}

      {data.fined.length > 0 && (
        <Card className="border-red-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Overdue — fine applies</CardTitle>
            <CardDescription>
              You can still mark mantras written. Coordinator can waive the
              fine after confirming.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {data.fined.map((day) => (
              <label
                key={day.date}
                className="flex items-start gap-2 rounded-md border border-red-100 p-2 text-sm"
              >
                <Checkbox
                  checked={selected.includes(day.date)}
                  onCheckedChange={() => toggle(day.date)}
                />
                <span>
                  <span className="font-medium">{dayLabel(day)}</span>
                  <span className="mt-0.5 block text-xs text-red-700">
                    ₹{day.fine_rupees} due · was due {formatDateDisplay(day.due_by)}
                  </span>
                </span>
              </label>
            ))}
          </CardContent>
        </Card>
      )}

      {selectable.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Confirm write-up</CardTitle>
          </CardHeader>
          <CardContent>
            <form
              className="space-y-3"
              onSubmit={(e) => {
                e.preventDefault();
                if (selected.length === 0) return;
                submit.mutate();
              }}
            >
              <div className="space-y-2">
                <Label htmlFor="mantra-notes">Notes (optional)</Label>
                <Textarea
                  id="mantra-notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="e.g. Written and given to coordinator"
                />
              </div>
              {submit.isError && (
                <p className="text-sm text-red-600">Could not submit.</p>
              )}
              {submit.isSuccess && (
                <p className="text-sm text-emerald-700">
                  Submitted — waiting for coordinator confirmation.
                </p>
              )}
              <Button
                type="submit"
                className="w-full"
                disabled={submit.isPending || selected.length === 0}
              >
                {submit.isPending
                  ? "Submitting…"
                  : `I have written mantras (${selected.length})`}
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      {data.submitted.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm font-medium">Waiting for confirmation</h3>
          <ul className="space-y-2">
            {data.submitted.map((day) => (
              <li
                key={day.date}
                className="flex items-center justify-between rounded-lg border p-3 text-sm"
              >
                <span>{dayLabel(day)}</span>
                <Badge>Pending</Badge>
              </li>
            ))}
          </ul>
        </div>
      )}

      {data.waived.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm font-medium">Confirmed — no fine</h3>
          <ul className="space-y-2">
            {data.waived.map((day) => (
              <li
                key={day.date}
                className="flex items-center justify-between rounded-lg border p-3 text-sm text-muted-foreground"
              >
                <span>{dayLabel(day)}</span>
                <Badge variant="secondary">Waived</Badge>
              </li>
            ))}
          </ul>
        </div>
      )}

      <Button variant="outline" className="w-full" asChild>
        <Link to="/me">Back to today</Link>
      </Button>
    </div>
  );
}
